import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { CardTitle, GridCard } from '@components/GridCards/Common.styled';
import { CardHeader, CardContent, ContentRow, Exclamation, HeaderCol, MapText } from './HeatMap.styled';
import Tooltip from '@components/Tooltips/Tooltip';
import Exclam_SVG from '@assets/svg/exclamation.svg';
import db from '@utils/db';
import { useStore } from '@store/appStore';
import moment from 'moment';
import MapItem from '@components/GridCards/MapItem';
import { useI18n } from '@i18n/useI18n';
import CardInfo from '../CardInfo';
import useOnScreen from '@components/Utils/useOnScreen';
import RainbowLoader from '@components/Loaders/RainbowLoader';
import 'moment/locale/de'
import 'moment/locale/es'
import 'moment/locale/nl'
import 'moment/locale/fi'
import 'moment/locale/sv'
import 'moment/locale/nb'
import 'moment/locale/fr'
import 'moment/locale/it'
import 'moment/locale/pt'
import 'moment/locale/tr'
import 'moment/locale/is'
import toTitleCase from '@utils/textTools';

interface IProps {
  id: any;
  updateTime?: any;
}

export const HeatMap = (props: IProps) => {
  const { callNameSetting, settings, rewardScreen, setRewardScreen, setRewardScreenBadge, client, ids, filterDate, semesters } = useStore();
  const [heatMap, setHeatMap] = useState<any>([]);
  // heatMap's own [] initial state can't tell "not loaded yet" apart from
  // "loaded, genuinely nothing to show" - loading tracks that separately.
  const [loading, setLoading] = useState(true);
  const i18n = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref);
  const loadingData = useRef(false)

  useEffect(() => {
    if (isVisible && !loadingData.current) {
      loadingData.current = true
      getData();
    }
  }, [isVisible, props.id])

  useEffect(() => {
    if (loadingData.current) {
      loadingData.current = true
      getData();
    }
  }, [filterDate, props.id, props.updateTime])


  const tempHeatMap = [
    {
      starttime: 8,
      endtime: 10,
    },
    {
      starttime: 10,
      endtime: 12,
    },
    {
      starttime: 12,
      endtime: 14,
    },
    {
      starttime: 14,
      endtime: 16,
    },
    {
      starttime: 16,
      endtime: 18,
    },
    {
      starttime: 18,
      endtime: 20,
    },
  ];

  const getData = async () => {
    let from = filterDate.from;
    let to = filterDate.to;

    if (!to) to = moment().format('YYYY-MM-DD');
    if (!from) from = moment().add(-55, 'years').format('YYYY-MM-DD');

    let data = await db.Student_Stat_GetHeatMapDetails(props.id, from, to, settings.USEMERIT, settings.USEPAUSE);
    // No answer (e.g. the connection dropped): keep what's shown - the profile's update check reloads
    // the cards once the connection is back.
    if (!data) {
      setLoading(false);
      return;
    }
    let tempData = { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] };
    let pauseData = []
    let studentInfo = await db.getUserInfo(props.id)

    if (settings.USENOTFULLBLOCKPAUSE && studentInfo?.length > 0 && studentInfo[0].student_id) {
      pauseData = (await db.GetPause4HeatMap(studentInfo[0].student_id, from, to)) ?? []
    }

    data.forEach((d) => {
      let day = pauseData.find(p => p.weekdaynumber === d.weekdaynumber && d.starthour === p.starthour)
      if (day) {
        d.absence_duration = d.absence_duration - day.pause_absence_length
        d.duration = d.duration - day.pause_length
      }

      switch (d.weekdaynumber) {
        case 1:
          tempData.monday.push(d);
          break;
        case 2:
          tempData.tuesday.push(d);
          break;
        case 3:
          tempData.wednesday.push(d);
          break;
        case 4:
          tempData.thursday.push(d);
          break;
        case 5:
          tempData.friday.push(d);
          break;
        case 6:
          tempData.saturday.push(d);
          break;
        case 7:
          tempData.sunday.push(d);
          break;
      }
    });

    let heatMap = [];

    for (let i = 0; i < tempHeatMap.length; i++) {
      let item = tempHeatMap[i];

      let monday = tempData.monday.filter((d) => d.starthour >= item.starttime && d.starthour < item.endtime);
      let tuesday = tempData.tuesday.filter((d) => d.starthour >= item.starttime && d.starthour < item.endtime);
      let wednesday = tempData.wednesday.filter((d) => d.starthour >= item.starttime && d.starthour < item.endtime);
      let thursday = tempData.thursday.filter((d) => d.starthour >= item.starttime && d.starthour < item.endtime);
      let friday = tempData.friday.filter((d) => d.starthour >= item.starttime && d.starthour < item.endtime);
      let saturday = tempData.saturday.filter((d) => d.starthour >= item.starttime && d.starthour < item.endtime);
      let sunday = tempData.sunday.filter((d) => d.starthour >= item.starttime && d.starthour < item.endtime);

      let array = [];
      array.push(getAbsence(monday));
      array.push(getAbsence(tuesday));
      array.push(getAbsence(wednesday));
      array.push(getAbsence(thursday));
      array.push(getAbsence(friday));
      array.push(getAbsence(saturday));
      array.push(getAbsence(sunday));

      heatMap.push({
        time: item.starttime.toString() + ':00',
        dataset: array,
      });
    }

    setHeatMap(heatMap);
    setLoading(false);
  };

  const getAbsence = (array) => {
    let temp = {
      duration: 0,
      lessons: 0,
      absence_duration: 0,
      absence_lessons: 0,
      lessonspercentage: 0,
      secondpercentage: 0,
    };

    for (let j = 0; j < array.length; j++) {
      let item = array[j];
      temp.duration += item.duration;
      temp.lessons += item.lessons;
      temp.absence_duration += item.absence_duration;
      temp.absence_lessons += item.absence_lessons;
    }

    temp.secondpercentage = temp.absence_duration > 0 ? temp.absence_duration / temp.duration : 0;
    temp.lessonspercentage = temp.absence_lessons > 0 ? temp.absence_lessons / temp.lessons : 0;
    return temp;
  };

  const dayActive = (day) => {
    let lessons = heatMap.map((d) => (d.dataset[day].lessons ? d.dataset[day].lessons : 0));
    let sum = 0;
    for (let i = 0; i < lessons.length; i++) {
      sum += lessons[i];
    }
    return sum === 0;
  };

  const getLanguage = () => {
    if (i18n.language === 'no') return 'nb'
    else if (i18n.language === 'se') return 'sv'
    else return i18n.language
  }

  return (
    <GridCard ref={ref} col={2} heightAuto={true}>
      <CardHeader>
        <HeaderCol></HeaderCol>
        <HeaderCol>
          <CardTitle>{i18n.t('heatmap-header')}</CardTitle>
        </HeaderCol>
        <HeaderCol>
          <CardInfo padding={true} text='heatmap-info' />
        </HeaderCol>
      </CardHeader>
      <CardContent rows={tempHeatMap.length + 1}>
        <ContentRow>
          <MapText></MapText>
          <MapText disable={dayActive(0)}>{toTitleCase(moment().locale(getLanguage()).startOf('week').isoWeekday(1).format('dddd').substring(0, 3))}</MapText>
          <MapText disable={dayActive(1)}>{toTitleCase(moment().locale(getLanguage()).startOf('week').isoWeekday(2).format('dddd').substring(0, 3))}</MapText>
          <MapText disable={dayActive(2)}>{toTitleCase(moment().locale(getLanguage()).startOf('week').isoWeekday(3).format('dddd').substring(0, 3))}</MapText>
          <MapText disable={dayActive(3)}>{toTitleCase(moment().locale(getLanguage()).startOf('week').isoWeekday(4).format('dddd').substring(0, 3))}</MapText>
          <MapText disable={dayActive(4)}>{toTitleCase(moment().locale(getLanguage()).startOf('week').isoWeekday(5).format('dddd').substring(0, 3))}</MapText>
          <MapText disable={dayActive(5)}>{toTitleCase(moment().locale(getLanguage()).startOf('week').isoWeekday(6).format('dddd').substring(0, 3))}</MapText>
          <MapText disable={dayActive(6)}>{toTitleCase(moment().locale(getLanguage()).startOf('week').isoWeekday(0).format('dddd').substring(0, 3))}</MapText>
        </ContentRow>
        {loading && <RainbowLoader />}
        {heatMap.map((m, row) => {
          return (
            <ContentRow key={'heatmap-' + row}>
              <MapText>{m.time}</MapText>
              {m.dataset.map((i, index) => {
                if (dayActive(index)) {
                  return <MapItem key={"heatmap-" + row + '-' + index} isvisible={isVisible} index={index} disable={true} row={row} />;
                } else {
                  let percentage = (settings.USE_LESSON_BASED_ABSENCE === 'true' ? i.lessonspercentage : i.secondpercentage);
                  return (
                    <MapItem
                      key={"heatmap-" + row + '-' + index}
                      isvisible={isVisible}
                      row={row}
                      index={index}
                      data={i}
                      opacity={percentage > 0 ? (percentage * 4).toString() : 0}
                    />
                  );
                }
              })}
            </ContentRow>
          );
        })}
      </CardContent>
    </GridCard>
  );
};

export default HeatMap;
